import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from 'assets/styles/theme';
import { analyticsService } from 'services/analytics';
import PaginationBlock from 'common/pagination';
import PaginationLoader from 'components/loader/paginationLoader';
import TableResultSelection from 'common/table-results-selection';
import FormatDate from 'common/format-date';
import { TableBlock } from 'assets/styles/table-styles';
import { EMPTY_LIST_PLACEHOLDER } from 'utils/values';
import { messageNotification } from 'utils/functions';
import {
  StyledCourseStats,
  TableHeader,
  DataBlock,
  SearchTagsList,
} from './styled';

const DEFAULT_PAGE_SIZE = 10;

const DownloadLink = styled.button`
  background: none;
  border: none;
  height: auto;
  padding: 0;
  color: ${theme.secondary};
  font-size: ${theme.baseFontSize};
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${theme.text};
  }
`;

/**
 * Monthly Active Users reports table. Scoped server-side to the viewer's
 * organization (the admin's selected org, the overall report when no org is
 * selected, or a course-staff member's own org). The App remounts routed
 * content on org change, so this refetches automatically for the chosen org.
 */
const MAUReports = () => {
  const [reports, setReports] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [inProgress, setProgress] = useState(false);

  const fetchReports = (pageNumber, size) => {
    setProgress(true);
    analyticsService
      .fetchMAUReports({ page: pageNumber, page_size: size })
      .then((response) => {
        setReports(response.data.results || []);
        setCount(response.data.count || 0);
        setProgress(false);
      })
      .catch(() => {
        setReports([]);
        setCount(0);
        setProgress(false);
      });
  };

  useEffect(() => {
    fetchReports(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (property, value) => {
    setPage(value);
    fetchReports(value, pageSize);
  };

  const handlePageSize = (size) => {
    setPageSize(size);
    setPage(1);
    fetchReports(1, size);
  };

  const handleDownload = (report) => {
    analyticsService
      .downloadMAUReport(report.id)
      .then((response) => {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `mau_${report.organization}_${report.month}.csv`.replace(/\s+/g, '_');
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => {
        messageNotification('danger', 'Error', 'Unable to download the report. Please try again.');
      });
  };

  return (
    <StyledCourseStats>
      <TableHeader>
        <h2>Monthly Active Users Reports</h2>
        {inProgress && <PaginationLoader />}
        <DataBlock className="v2">
          <SearchTagsList />
          <TableResultSelection handleOnChange={handlePageSize} pageSize={DEFAULT_PAGE_SIZE} />
        </DataBlock>
      </TableHeader>
      <TableBlock className="course-table responsive">
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th>Organization</th>
              <th>Active Learners</th>
              <th>Excluded Staff</th>
              <th>Generated</th>
              <th>Report</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr key={report.id}>
                <td data-title="Month">
                  <FormatDate format="MMM YYYY">{`${report.month}-01`}</FormatDate>
                </td>
                <td data-title="Organization">{report.organization}</td>
                <td data-title="Active Learners">{report.active_user_count}</td>
                <td data-title="Excluded Staff">
                  {report.excluded_staff_count || EMPTY_LIST_PLACEHOLDER}
                </td>
                <td data-title="Generated">
                  <FormatDate format="Do MMM YYYY">{report.created_at}</FormatDate>
                </td>
                <td data-title="Report">
                  <DownloadLink type="button" onClick={() => handleDownload(report)}>
                    Download CSV
                  </DownloadLink>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableBlock>
      {reports.length === 0 && !inProgress && (
        <div className="no-data">No results to show</div>
      )}
      {count > 0 && (
        <PaginationBlock
          activePage={page}
          itemsCountPerPage={pageSize}
          totalItemsCount={count}
          pageRangeDisplayed={5}
          onPageChange={handlePageChange}
          showPageCounter
        />
      )}
    </StyledCourseStats>
  );
};

export default MAUReports;
